import type { KeyboardEvent, HTMLAttributes } from "react";
import { format, parseISO } from "date-fns";

import type {
  ActivityRow,
  ActivityTab,
  CurrencyRow,
  DashboardKpis,
  PhysicalTotals,
  VolumeKpi,
  WeightUnit,
} from "./dashboardTypes";

export const WEIGHT_STORAGE_KEY = "dashboardWeightUnit";

export const INVENTORY_COLOR_BY_KEY: Record<string, string> = {
  available: "#10b981",
  shipped: "#3b82f6",
  in_wip: "#f59e0b",
  locked_cycle: "#64748b",
  missing: "#f43f5e",
  created: "#3b82f6",
  in_process: "#f59e0b",
  completed: "#10b981",
  __other__: "#94a3b8",
};

export function readStoredWeightUnit(): WeightUnit {
  if (typeof window === "undefined") return "kg";
  return localStorage.getItem(WEIGHT_STORAGE_KEY) === "lbs" ? "lbs" : "kg";
}

export const NA_MONEY = "N/A";

export function normalizeCurrencyCode(raw: unknown): string | null {
  if (raw == null || String(raw).trim() === "") return null;
  const c = String(raw).trim().toUpperCase();
  if (!/^[A-Z]{3}$/.test(c)) return null;
  try {
    new Intl.NumberFormat("en-US", { style: "currency", currency: c }).format(
      0,
    );
    return c;
  } catch {
    return null;
  }
}

export function currencyLabelFromRow(row: unknown): string | null {
  if (!row || typeof row !== "object") return null;
  const r = row as CurrencyRow;
  if (r.currency_code != null && String(r.currency_code).trim() !== "") {
    return String(r.currency_code).trim();
  }
  if (r.currency != null && String(r.currency).trim() !== "") {
    return String(r.currency).trim();
  }
  return null;
}

export function formatSummaryMoney(
  n: unknown,
  currencySource: unknown,
  fractionDigits = 0,
): string {
  if (n == null || Number.isNaN(Number(n))) return NA_MONEY;
  let raw: string | null = null;
  if (currencySource != null && typeof currencySource === "object") {
    raw = currencyLabelFromRow(currencySource);
  } else if (currencySource != null && String(currencySource).trim() !== "") {
    raw = String(currencySource).trim();
  }
  const currency = normalizeCurrencyCode(raw);
  if (!currency) return NA_MONEY;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(Number(n));
}

export function pickReceivingKpi(
  kpis: DashboardKpis | undefined,
): CurrencyRow | undefined {
  return kpis?.receiving_total ?? kpis?.receiving_total_usd;
}

export function receivingAmount(rt: CurrencyRow | undefined): number | undefined {
  if (!rt) return undefined;
  return rt.total ?? rt.total_usd;
}

export function pickProductionCostKpi(
  kpis: DashboardKpis | undefined,
): CurrencyRow | undefined {
  return kpis?.production_cost ?? kpis?.production_cost_usd;
}

export function pickPackagingVolumeKpi(
  kpis: DashboardKpis | undefined,
): VolumeKpi | undefined {
  return kpis?.packaging_volume;
}

export function productionAmount(
  pc: CurrencyRow | undefined,
): number | undefined {
  if (!pc) return undefined;
  return pc.total ?? pc.total_usd;
}

export function pickShippedSalesKpi(
  kpis: DashboardKpis | undefined,
): CurrencyRow | undefined {
  return kpis?.shipped_sales;
}

export function shippedAllocatedAmount(
  ss: CurrencyRow | undefined,
): number | undefined {
  if (!ss) return undefined;
  const root = ss.allocated;
  if (root != null) return Number(root);
  const rows = ss.by_currency;
  if (!Array.isArray(rows) || rows.length === 0) return undefined;
  return rows.reduce((s, r) => s + (Number(r.allocated) || 0), 0);
}

export function sumBucketsPhysical(
  rows: CurrencyRow[] | undefined,
  root: CurrencyRow | undefined,
): PhysicalTotals {
  if (!Array.isArray(rows) || rows.length === 0) {
    return {
      weight_kg: Number(root?.weight_kg) || 0,
      weight_lbs: Number(root?.weight_lbs) || 0,
      pieces: Number(root?.pieces) || 0,
    };
  }
  return rows.reduce<PhysicalTotals>(
    (acc, r) => ({
      weight_kg: acc.weight_kg + (Number(r.weight_kg) || 0),
      weight_lbs: acc.weight_lbs + (Number(r.weight_lbs) || 0),
      pieces: acc.pieces + (Number(r.pieces) || 0),
    }),
    { weight_kg: 0, weight_lbs: 0, pieces: 0 },
  );
}

export function topShippedSalesBucketsCadPrimary(
  rows: CurrencyRow[] | undefined,
  limit = 3,
): CurrencyRow[] | null {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const isCad = (r: CurrencyRow) =>
    String(r.currency_code ?? "").toUpperCase() === "CAD" ||
    String(r.currency ?? "").toUpperCase() === "CAD";
  const cadRows = rows.filter(isCad);
  const rest = rows.filter((r) => !isCad(r));
  rest.sort((a, b) => (Number(b.allocated) || 0) - (Number(a.allocated) || 0));
  const ordered = cadRows.length > 0 ? [cadRows[0], ...rest] : rows.slice();
  return ordered.slice(0, limit);
}

export function formatShipmentOrderTotal(row: ActivityRow): string {
  return formatSummaryMoney(row.total ?? row.total_usd, row, 2);
}

export function formatOrderShipmentStatus(row: ActivityRow): string {
  if (row.status != null && String(row.status).trim() !== "") {
    return String(row.status);
  }
  if (row.status_code != null) {
    return `Unknown (${row.status_code})`;
  }
  return "—";
}

export function formatInt(n: unknown): string {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 });
}

export function activityTabCount(
  counts: Record<string, number | undefined> | undefined,
  tab: ActivityTab,
): number | undefined {
  const v = counts?.[tab.countKey];
  if (v != null) return v;
  if (tab.legacyCountKey) return counts?.[tab.legacyCountKey];
  return undefined;
}

export function formatInvoiceWeightCell(row: ActivityRow, weightKg: boolean): string {
  const kg = row.total_kg ?? row.weight_kg;
  const lbs = row.total_lbs;
  if (weightKg) {
    if (kg == null) return "—";
    return `${formatInt(kg)} kg`;
  }
  if (lbs != null) {
    return `${Number(lbs).toLocaleString("en-US", {
      maximumFractionDigits: 2,
    })} lbs`;
  }
  if (kg != null) {
    return `${(Number(kg) * 2.2046226218).toLocaleString("en-US", {
      maximumFractionDigits: 2,
    })} lbs`;
  }
  return "—";
}

export function formatInvoicePiecesCell(row: ActivityRow): string {
  const p = row.total_pieces;
  if (p == null || Number(p) === 0) return "—";
  return formatInt(p);
}

export function formatInvoiceTotalUsdCell(row: ActivityRow): string {
  return formatSummaryMoney(
    row.total ?? row.total_usd ?? row.total_cost,
    row,
    2,
  );
}

export function creditReturnNavPath(row: ActivityRow): string | null {
  if (row.credit_return_id != null && row.credit_return_id !== "") {
    return `/credit-returns/${row.credit_return_id}`;
  }
  if (row.order_id != null) return `/shipments/${row.order_id}`;
  return null;
}

export function formatActivityProductionDate(raw: unknown): string {
  if (raw == null || String(raw).trim() === "") return "—";
  const s = String(raw).trim();
  try {
    return format(parseISO(s), "MMM d, yyyy");
  } catch {
    return s;
  }
}

export function formatCompactKg(totalKg: unknown): string {
  const v = Number(totalKg);
  if (Number.isNaN(v)) return "—";
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M kg`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}k kg`;
  return `${v.toLocaleString("en-US", { maximumFractionDigits: 1 })} kg`;
}

export const ROW_LINK_FOCUS =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-blue-500";

export function openAppPathInNewTab(path: string | null | undefined): void {
  if (!path || typeof window === "undefined") return;
  const absolute =
    path.startsWith("http://") || path.startsWith("https://")
      ? path
      : `${window.location.origin}${path.startsWith("/") ? path : `/${path}`}`;
  const w = window.open(absolute, "_blank", "noopener,noreferrer");
  if (w) w.opener = null;
}

export function newTabRowHandlers(
  path: string | null,
  ariaLabel?: string,
): HTMLAttributes<HTMLTableRowElement> {
  if (!path) return {};
  return {
    tabIndex: 0,
    role: "link",
    "aria-label": ariaLabel,
    onClick: () => openAppPathInNewTab(path),
    onKeyDown: (e: KeyboardEvent<HTMLTableRowElement>) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openAppPathInNewTab(path);
      }
    },
  };
}

export const ACTIVITY_TABS: ActivityTab[] = [
  {
    id: "recent-invoices",
    label: "Recent invoices",
    countKey: "recent-invoices",
    legacyCountKey: "recent-receiving",
  },
  {
    id: "recent-production",
    label: "Recent production",
    countKey: "recent_productions",
    legacyCountKey: "recent_productions",
  },
];
